import { ofetch } from 'ofetch';

import { createSingleton } from '@deepidoo/deesplay-shared/utils';

import { useOrganizationStore } from '../../stores';
import { useApiError, useToast } from '../../composables';
import { useSession } from './useSession';
import { toastMessages } from '../../utils';

enum HttpMethod {
    GET = 'GET',
    POST = 'POST',
    PUT = 'PUT',
    PATCH = 'PATCH',
    DELETE = 'DELETE'
};

export interface HttpQueryParam {
    key: string;
    value?: string[] | number[] | boolean[] | string | number | boolean;
};

export const useFetchClient = createSingleton((): {
    constructUrl: (url: string, params?: HttpQueryParam[]) => string;
    postWithFile: <T>(url: string, file?: File, body?: any, params?: HttpQueryParam[], options?: RequestInit) => Promise<T>;
    putWithFile: <T>(url: string, file?: File, body?: any, params?: HttpQueryParam[], options?: RequestInit) => Promise<T>;
    get: <T>(url: string, params?: HttpQueryParam[], options?: RequestInit) => Promise<T>;
    post: <T>(url: string, body?: any, params?: HttpQueryParam[], options?: RequestInit) => Promise<T>;
    put: <T>(url: string, body?: any, params?: HttpQueryParam[], options?: RequestInit) => Promise<T>;
    patch: <T>(url: string, body?: any, params?: HttpQueryParam[], options?: RequestInit) => Promise<T>;
    remove: (url: string, params?: HttpQueryParam[], options?: RequestInit) => Promise<void>;
} => {
    const { handleApiError } = useApiError();
    const { showToast } = useToast();

    const fetchClient = ofetch.create({
        onRequest: ({ options, request }) => {
            if (request.toString().startsWith(`${import.meta.env.VITE_API_BASE_URL}${import.meta.env.VITE_API_URL_SUFFIX}`)) {
                const { currentOrganization } = useOrganizationStore();
                const { accessToken, isAuthenticated } = useSession();

                if (currentOrganization) {
                    options.headers.set('X-Organization-Id', currentOrganization.id.toString());
                }
                if (isAuthenticated.value) {
                    options.headers.set('Authorization', `Bearer ${accessToken.value}`);
                }
            }
        },
        onResponseError: ({ response }): void => {
            handleApiError(response.status);
        }
    });

    const timedRequest = async <T>(
        method: HttpMethod,
        url: string,
        body?: any,
        params?: HttpQueryParam[],
        options?: RequestInit
    ): Promise<T> => {
        const start = performance.now();

        const result = await fetchClient(constructUrl(url, params), {
            method,
            body: body ? JSON.stringify(body) : undefined,
            ...options
        });

        const duration = performance.now() - start;

        if (duration > 5000) {
            showToast(toastMessages.warning.networkSlow);
        }

        return result;
    };

    const constructUrl = (url: string, params?: HttpQueryParam[]): string => {
        let fullUrl = url;
        if (params && params.length > 0) {
            const queryString = params
                .filter(param => {
                    if (param.value === undefined) {
                        return false;
                    }
                    if (typeof param.value === 'string' && param.value.trim() === '') {
                        return false;
                    }
                    if (Array.isArray(param.value) && param.value.length === 0) {
                        return false;
                    }
                    return true;
                })
                .map(param => `${encodeURIComponent(param.key)}=${encodeURIComponent(param.value as string | number | boolean)}`)
                .join('&');
            fullUrl += `?${queryString}`;
        }
        return fullUrl;
    };

    const postWithFile = <T>(url: string, file?: File, body?: any, params?: HttpQueryParam[], options?: RequestInit): Promise<T> => {
        const formData = new FormData();
        if (file) {
            formData.append('file', file);
        }
        if (body) {
            formData.append('body', JSON.stringify(body));
        }

        return fetchClient(constructUrl(url, params), {
            method: HttpMethod.POST,
            body: formData,
            ...options
        });
    };

    const putWithFile = <T>(url: string, file?: File, body?: any, params?: HttpQueryParam[], options?: RequestInit): Promise<T> => {
        const formData = new FormData();
        if (file) {
            formData.append('file', file);
        }
        if (body) {
            formData.append('body', JSON.stringify(body));
        }

        return fetchClient(constructUrl(url, params), {
            method: HttpMethod.PUT,
            body: formData,
            ...options
        });
    };

    const get = <T>(url: string, params?: HttpQueryParam[], options?: RequestInit): Promise<T> => {
        return timedRequest<T>(HttpMethod.GET, url, undefined, params, options);
    };

    const post = <T>(url: string, body: any, params?: HttpQueryParam[], options?: RequestInit): Promise<T> => {
        return timedRequest<T>(HttpMethod.POST, url, body, params, options);
    };

    const put = <T>(url: string, body: any, params?: HttpQueryParam[], options?: RequestInit): Promise<T> => {
        return timedRequest<T>(HttpMethod.PUT, url, body, params, options);
    };

    const patch = <T>(url: string, body: any, params?: HttpQueryParam[], options?: RequestInit): Promise<T> => {
        return timedRequest<T>(HttpMethod.PATCH, url, body, params, options);
    };

    const remove = (url: string, params?: HttpQueryParam[], options?: RequestInit): Promise<void> => {
        return timedRequest(HttpMethod.DELETE, url, undefined, params, options);
    };

    return {
        constructUrl,
        postWithFile,
        putWithFile,
        get,
        post,
        put,
        patch,
        remove
    };
});